import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

import { API_URL as API_BASE } from '../config/api';


const STATIC_PAGES = [
    { type: 'page', title: 'Nos Formations',        description: 'Catalogue complet des formations CINEF',                    path: '/formations',           icon: '🎓', tags: ['formation','catalogue','apprendre','cours'] },
    { type: 'page', title: 'Devenir Partenaire',     description: 'Rejoindre le réseau partenaire CINEF',                      path: '/devenir-partenaire',   icon: '🤝', tags: ['partenaire','consultant','entreprise','cabinet','rejoindre'] },
    { type: 'page', title: 'Nos Partenaires',        description: 'Découvrir les entreprises et organisations partenaires',    path: '/partenaires',          icon: '🏢', tags: ['partenaire','entreprise','réseau','confiance'] },
    { type: 'page', title: 'À propos de CINEF',      description: 'Notre mission, nos valeurs, notre histoire',               path: '/a-propos',             icon: '💡', tags: ['cinef','mission','valeurs','histoire','about'] },
    { type: 'page', title: 'Contact',                description: 'Nous contacter par email ou téléphone',                    path: '/contact',              icon: '📞', tags: ['contact','email','telephone','message'] },
    { type: 'page', title: 'Actualités',             description: 'Les dernières nouvelles et événements CINEF',              path: '/actualites',           icon: '📰', tags: ['actualité','news','événement','blog'] },
    { type: 'page', title: 'Espace Consultant',      description: 'Formulaire de candidature pour les consultants',           path: '/devenir-partenaire',   icon: '👤', tags: ['consultant','candidature','expert','profil'] },
    { type: 'page', title: 'Espace Entreprise',      description: 'Formulaire de candidature pour les entreprises',           path: '/devenir-partenaire',   icon: '🏛️', tags: ['entreprise','cabinet','candidature','profil'] },
    { type: 'page', title: 'Finance & Comptabilité', description: 'Formations en finance, comptabilité et audit',             path: '/formations?domaine=finance',    icon: '💰', tags: ['finance','comptabilité','audit','bilan','gestion'] },
    { type: 'page', title: 'Ressources Humaines',   description: 'Formations RH, recrutement et gestion du personnel',       path: '/formations?domaine=rh',         icon: '👥', tags: ['rh','ressources humaines','recrutement','personnel'] },
    { type: 'page', title: 'Management',             description: 'Formations management, leadership et gestion de projet',   path: '/formations?domaine=management', icon: '📊', tags: ['management','leadership','projet','équipe'] },
    { type: 'page', title: 'Marketing & Communication', description: 'Stratégie marketing, communication digitale',           path: '/formations?domaine=marketing',  icon: '📣', tags: ['marketing','communication','digital','brand'] },
    { type: 'page', title: 'Informatique & Digital', description: 'Formations IT, développement et cybersécurité',            path: '/formations?domaine=it',         icon: '💻', tags: ['informatique','it','digital','code','cyber'] },
    { type: 'page', title: 'Juridique & Conformité','description': 'Droit des affaires, conformité et réglementation',       path: '/formations?domaine=juridique',  icon: '⚖️', tags: ['juridique','droit','conformité','réglementation'] },
];

const SearchPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get('q') || searchParams.get('search') || '';

    const [query, setQuery]               = useState(initialQuery);
    const [inputValue, setInputValue]     = useState(initialQuery);
    const [formations, setFormations]     = useState([]);
    const [loading, setLoading]           = useState(false);
    const [hasSearched, setHasSearched]   = useState(!!initialQuery);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'formations' | 'pages'

    const inputRef = useRef(null);
    const timerRef = useRef(null);

    // ── Focus input au chargement ──
    useEffect(() => {
        if (!initialQuery) inputRef.current?.focus();
    }, []);

    // ── Fetch formations API ──
    const fetchFormations = useCallback(async (q) => {
        if (!q.trim()) { setFormations([]); return; }
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/formations`, { params: { search: q } });
            const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
            setFormations(data);
        } catch {
            setFormations([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Lancer recherche depuis URL param ──
    useEffect(() => {
        if (initialQuery) fetchFormations(initialQuery);
    }, [initialQuery]);

    // ── Debounce sur frappe ──
    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        clearTimeout(timerRef.current);
        if (val.trim().length >= 2) {
            timerRef.current = setTimeout(() => {
                setQuery(val);
                setHasSearched(true);
                fetchFormations(val);
                setSearchParams({ q: val });
            }, 400);
        } else if (!val.trim()) {
            setQuery('');
            setFormations([]);
            setHasSearched(false);
            setSearchParams({});
        }
    };

    // ── Soumission formulaire ──
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;
        clearTimeout(timerRef.current);
        setQuery(inputValue);
        setHasSearched(true);
        fetchFormations(inputValue);
        setSearchParams({ q: inputValue });
        inputRef.current?.blur();
    };

    // ── Effacer ──
    const handleClear = () => {
        setInputValue('');
        setQuery('');
        setFormations([]);
        setHasSearched(false);
        setSearchParams({});
        inputRef.current?.focus();
    };

    // ── Recherche pages statiques ──
    const matchingPages = query.trim()
        ? STATIC_PAGES.filter(p => {
            const q = query.toLowerCase();
            return (
                p.title.toLowerCase().includes(q) ||
                p.description.toLowerCase().includes(q) ||
                p.tags.some(t => t.includes(q))
            );
        })
        : [];

    // ── Couleur domaine ──
    const domainColor = (domain) => {
        const map = { management:'#198754', informatique:'#0d6efd', finance:'#6f42c1', rh:'#fd7e14', marketing:'#dc3545', droit:'#20c997', qualite:'#ffc107' };
        const key = (domain || '').toLowerCase();
        for (const [k, v] of Object.entries(map)) if (key.includes(k)) return v;
        return '#fd7e14';
    };

    // ── Highlight ──
    const highlight = (text, q) => {
        if (!q?.trim() || !text) return text;
        const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.split(regex).map((part, i) =>
            regex.test(part)
                ? <mark key={i} style={{ background: 'rgba(253,126,20,0.18)', color: '#e8620a', borderRadius: 3, padding: '0 2px', fontWeight: 700 }}>{part}</mark>
                : part
        );
    };

    const totalResults = formations.length + matchingPages.length;
    const showFormations = activeFilter === 'all' || activeFilter === 'formations';
    const showPages      = activeFilter === 'all' || activeFilter === 'pages';

    // ── Suggestions rapides ──
    const suggestions = ['Management', 'Finance', 'RH', 'Marketing', 'Informatique', 'Partenaire', 'Contact', 'Formations'];

    return (
        <>
            <Header />

            <div className="search-page">
                {/* ── Hero Search ── */}
                <div className="search-hero">
                    <div className="search-hero-bg">
                        <div className="blob blob-1" />
                        <div className="blob blob-2" />
                        <div className="grid-overlay" />
                    </div>

                    <div className="search-hero-content">
                        <div className="search-hero-label">
                            <span className="label-dot" />
                            Recherche globale
                        </div>
                        <h1 className="search-hero-title">
                            Trouvez ce que<br />
                            <span className="gradient-text">vous cherchez</span>
                        </h1>
                        <p className="search-hero-sub">
                            Formations, pages, rubriques — tout CINEF en une seule recherche
                        </p>

                        {/* ── Barre principale ── */}
                        <form className="main-search-form" onSubmit={handleSubmit}>
                            <div className={`main-search-bar ${inputValue ? 'has-value' : ''}`}>
                                <div className="msb-icon">
                                    {loading ? (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fd7e14" strokeWidth="2.5" strokeLinecap="round">
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" style={{ animation: 'spin .8s linear infinite' }} />
                                        </svg>
                                    ) : (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                                        </svg>
                                    )}
                                </div>

                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="msb-input"
                                    placeholder="Tapez une formation, une page, un domaine..."
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    autoComplete="off"
                                    autoFocus
                                />

                                {inputValue && (
                                    <button type="button" className="msb-clear" onClick={handleClear} title="Effacer">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                            <path d="M18 6 6 18M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}

                                <button type="submit" className="msb-submit">
                                    <span>Rechercher</span>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </form>

                        {/* ── Suggestions rapides ── */}
                        {!hasSearched && (
                            <div className="quick-suggestions">
                                <span className="qs-label">Suggestions :</span>
                                {suggestions.map(s => (
                                    <button
                                        key={s}
                                        className="qs-chip"
                                        onClick={() => {
                                            setInputValue(s);
                                            setQuery(s);
                                            setHasSearched(true);
                                            fetchFormations(s);
                                            setSearchParams({ q: s });
                                        }}
                                    >{s}</button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Résultats ── */}
                <div className="search-results-area">
                    <div className="results-container">

                        {/* ── État : pas encore cherché ── */}
                        {!hasSearched && (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e2e8f0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                                    </svg>
                                </div>
                                <h3>Commencez votre recherche</h3>
                                <p>Saisissez des mots-clés dans la barre ci-dessus pour explorer toutes les formations et pages du site.</p>
                            </div>
                        )}

                        {/* ── État : chargement ── */}
                        {hasSearched && loading && (
                            <div className="loading-state">
                                <div className="loading-spinner" />
                                <p>Recherche en cours…</p>
                            </div>
                        )}

                        {/* ── Résultats disponibles ── */}
                        {hasSearched && !loading && (
                            <>
                                {/* Barre de stats + filtres */}
                                <div className="results-topbar">
                                    <div className="results-stats">
                                        {totalResults > 0
                                            ? <><strong>{totalResults}</strong> résultat{totalResults > 1 ? 's' : ''} pour <em>« {query} »</em></>
                                            : <>Aucun résultat pour <em>« {query} »</em></>
                                        }
                                    </div>

                                    {totalResults > 0 && (
                                        <div className="filter-tabs">
                                            {[
                                                { key: 'all',        label: 'Tout',       count: totalResults },
                                                { key: 'formations', label: 'Formations', count: formations.length },
                                                { key: 'pages',      label: 'Pages',      count: matchingPages.length },
                                            ].map(tab => (
                                                <button
                                                    key={tab.key}
                                                    className={`filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
                                                    onClick={() => setActiveFilter(tab.key)}
                                                >
                                                    {tab.label}
                                                    {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Aucun résultat */}
                                {totalResults === 0 && (
                                    <div className="no-results">
                                        <div className="no-results-icon">🔍</div>
                                        <h3>Aucun résultat trouvé</h3>
                                        <p>Aucune formation ni page ne correspond à <strong>« {query} »</strong>.</p>
                                        <div className="no-results-tips">
                                            <p>Essayez :</p>
                                            <ul>
                                                <li>Des mots-clés plus simples (ex: "finance" plutôt que "formation en finance")</li>
                                                <li>De vérifier l'orthographe</li>
                                                <li>D'explorer directement <Link to="/formations">toutes nos formations</Link></li>
                                            </ul>
                                        </div>
                                        <div className="no-results-suggestions">
                                            {suggestions.map(s => (
                                                <button key={s} className="qs-chip" onClick={() => {
                                                    setInputValue(s); setQuery(s); fetchFormations(s); setSearchParams({ q: s });
                                                }}>{s}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── Bloc : Pages du site ── */}
                                {showPages && matchingPages.length > 0 && (
                                    <div className="results-block">
                                        <div className="block-header">
                                            <div className="block-header-icon pages-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                                                </svg>
                                            </div>
                                            <h2 className="block-title">Pages du site</h2>
                                            <span className="block-count">{matchingPages.length}</span>
                                        </div>

                                        <div className="pages-grid">
                                            {matchingPages.map((page, i) => (
                                                <Link key={i} to={page.path} className="page-card">
                                                    <div className="page-card-icon">{page.icon}</div>
                                                    <div className="page-card-body">
                                                        <div className="page-card-title">{highlight(page.title, query)}</div>
                                                        <div className="page-card-desc">{highlight(page.description, query)}</div>
                                                    </div>
                                                    <svg className="page-card-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M9 18l6-6-6-6" />
                                                    </svg>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── Bloc : Formations ── */}
                                {showFormations && formations.length > 0 && (
                                    <div className="results-block">
                                        <div className="block-header">
                                            <div className="block-header-icon formations-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
                                                </svg>
                                            </div>
                                            <h2 className="block-title">Formations</h2>
                                            <span className="block-count">{formations.length}</span>
                                        </div>

                                        <div className="formations-list">
                                            {formations.map((f) => {
                                                const color = domainColor(f.domain || f.domaine || '');
                                                return (
                                                    <div
                                                        key={f.id}
                                                        className="formation-card"
                                                        onClick={() => navigate(`/formations/${f.slug}`)}
                                                    >
                                                        <div className="fc-accent" style={{ background: color }} />
                                                        <div className="fc-icon" style={{ background: color + '15', color }}>
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
                                                            </svg>
                                                        </div>
                                                        <div className="fc-body">
                                                            <h3 className="fc-title">{highlight(f.title || f.titre, query)}</h3>
                                                            <div className="fc-meta">
                                                                {(f.domain || f.domaine) && (
                                                                    <span className="fc-tag" style={{ background: color + '12', color }}>{f.domain || f.domaine}</span>
                                                                )}
                                                                {(f.level || f.niveau) && (
                                                                    <span className="fc-tag fc-tag-neutral">{f.level || f.niveau}</span>
                                                                )}
                                                                {(f.duration || f.duree) && (
                                                                    <span className="fc-tag fc-tag-neutral">⏱ {f.duration || f.duree}</span>
                                                                )}
                                                            </div>
                                                            {f.description && (
                                                                <p className="fc-desc">
                                                                    {f.description.length > 120 ? f.description.slice(0, 120) + '…' : f.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="fc-cta">
                                                            <span>Voir</span>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M5 12h14M12 5l7 7-7 7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Lien voir toutes */}
                                        <div className="see-all-wrapper">
                                            <Link to={`/formations?search=${encodeURIComponent(query)}`} className="see-all-btn">
                                                Voir toutes les formations pour « {query} »
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Footer />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

                @keyframes spin       { to { transform: rotate(360deg); } }
                @keyframes fadeUp     { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
                @keyframes blobFloat  { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(40px,-40px) scale(1.08)} }
                @keyframes gradShift  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
                @keyframes pulse-dot  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(1.4)} }

                *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }

                .search-page {
                    font-family: 'DM Sans', -apple-system, sans-serif;
                    min-height: 100vh;
                    background: #f8f9fb;
                }

                /* ══ HERO ══ */
                .search-hero {
                    position: relative;
                    padding: 120px 24px 80px;
                    background: linear-gradient(160deg, #0f1923 0%, #1a2d20 50%, #0f1923 100%);
                    overflow: hidden;
                }

                .search-hero-bg { position:absolute; inset:0; pointer-events:none; }

                .blob {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(80px);
                    opacity: 0.35;
                }
                .blob-1 {
                    width: 500px; height: 500px;
                    background: radial-gradient(circle, #fd7e14 0%, transparent 70%);
                    top: -150px; right: -100px;
                    animation: blobFloat 18s ease-in-out infinite;
                }
                .blob-2 {
                    width: 400px; height: 400px;
                    background: radial-gradient(circle, #198754 0%, transparent 70%);
                    bottom: -120px; left: -80px;
                    animation: blobFloat 22s ease-in-out infinite reverse;
                }

                .grid-overlay {
                    position: absolute; inset: 0;
                    background-image: linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px),
                                      linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px);
                    background-size: 48px 48px;
                }

                .search-hero-content {
                    position: relative; z-index: 1;
                    max-width: 760px; margin: 0 auto;
                    text-align: center;
                    animation: fadeUp .7s ease-out;
                }

                .search-hero-label {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 8px 18px;
                    background: rgba(255,255,255,.08);
                    border: 1px solid rgba(255,255,255,.12);
                    border-radius: 50px;
                    font-size: 13px; font-weight: 600; color: #94a3b8;
                    letter-spacing: .5px; text-transform: uppercase;
                    margin-bottom: 28px;
                }

                .label-dot {
                    width: 7px; height: 7px;
                    background: #fd7e14; border-radius: 50%;
                    animation: pulse-dot 2s ease-in-out infinite;
                }

                .search-hero-title {
                    font-family: 'Sora', sans-serif;
                    font-size: clamp(36px, 5vw, 58px);
                    font-weight: 800;
                    color: white;
                    line-height: 1.15;
                    letter-spacing: -1.5px;
                    margin-bottom: 16px;
                }

                .gradient-text {
                    background: linear-gradient(135deg, #fd7e14 0%, #ffc107 50%, #fd7e14 100%);
                    background-size: 200% 200%;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    animation: gradShift 4s ease infinite;
                }

                .search-hero-sub {
                    font-size: 17px; color: #64748b; margin-bottom: 40px; line-height: 1.6;
                }

                /* ── Barre principale ── */
                .main-search-form { width: 100%; margin-bottom: 24px; }

                .main-search-bar {
                    display: flex; align-items: center;
                    background: rgba(255,255,255,.06);
                    border: 1.5px solid rgba(255,255,255,.12);
                    border-radius: 18px;
                    padding: 6px 6px 6px 20px;
                    gap: 8px;
                    transition: all .3s ease;
                    backdrop-filter: blur(12px);
                }
                .main-search-bar:focus-within, .main-search-bar.has-value {
                    background: rgba(255,255,255,.1);
                    border-color: #fd7e14;
                    box-shadow: 0 0 0 4px rgba(253,126,20,.15);
                }

                .msb-icon { color: #64748b; display:flex; align-items:center; flex-shrink:0; transition: color .3s; }
                .main-search-bar:focus-within .msb-icon { color: #fd7e14; }

                .msb-input {
                    flex: 1; border: none; outline: none;
                    background: transparent;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 17px; color: white;
                    padding: 14px 8px;
                }
                .msb-input::placeholder { color: #475569; }

                .msb-clear {
                    background: rgba(255,255,255,.08); border: none; cursor: pointer;
                    color: #64748b; width: 32px; height: 32px; border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    transition: all .2s; flex-shrink: 0;
                }
                .msb-clear:hover { background: rgba(255,255,255,.15); color: white; }

                .msb-submit {
                    display: flex; align-items: center; gap: 10px;
                    padding: 14px 28px;
                    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
                    border: none; border-radius: 13px;
                    font-family: 'Sora', sans-serif;
                    font-size: 15px; font-weight: 700; color: white;
                    cursor: pointer; transition: all .3s; flex-shrink: 0;
                }
                .msb-submit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(253,126,20,.4); }
                .msb-submit:active { transform: translateY(0); }

                /* ── Suggestions rapides ── */
                .quick-suggestions {
                    display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
                    justify-content: center;
                }
                .qs-label { font-size: 13px; color: #64748b; font-weight: 500; }
                .qs-chip {
                    padding: 7px 16px;
                    background: rgba(255,255,255,.07);
                    border: 1px solid rgba(255,255,255,.12);
                    border-radius: 50px;
                    font-size: 13px; font-weight: 600; color: #94a3b8;
                    cursor: pointer; transition: all .25s;
                    font-family: 'DM Sans', sans-serif;
                }
                .qs-chip:hover {
                    background: rgba(253,126,20,.15);
                    border-color: #fd7e14; color: #fd7e14;
                    transform: translateY(-2px);
                }

                /* ══ ZONE RÉSULTATS ══ */
                .search-results-area {
                    padding: 48px 24px 80px;
                    min-height: 400px;
                }
                .results-container { max-width: 900px; margin: 0 auto; }

                /* ── États vides ── */
                .empty-state, .loading-state, .no-results {
                    text-align: center; padding: 80px 24px;
                    animation: fadeUp .5s ease-out;
                }
                .empty-icon { margin-bottom: 20px; }
                .empty-state h3, .no-results h3 {
                    font-family: 'Sora', sans-serif;
                    font-size: 22px; font-weight: 700; color: #1e293b; margin-bottom: 10px;
                }
                .empty-state p, .no-results p { font-size: 15px; color: #64748b; line-height: 1.6; }

                .loading-state {
                    display: flex; flex-direction: column; align-items: center; gap: 16px;
                }
                .loading-spinner {
                    width: 44px; height: 44px;
                    border: 3px solid #e2e8f0;
                    border-top-color: #fd7e14;
                    border-radius: 50%;
                    animation: spin .7s linear infinite;
                }
                .loading-state p { color: #64748b; font-size: 15px; }

                .no-results-icon { font-size: 56px; margin-bottom: 16px; }
                .no-results-tips {
                    background: #f1f5f9; border-radius: 12px; padding: 20px 24px;
                    text-align: left; max-width: 480px; margin: 24px auto;
                }
                .no-results-tips p { font-weight: 600; color: #475569; margin-bottom: 8px; }
                .no-results-tips ul { padding-left: 18px; color: #64748b; font-size: 14px; line-height: 2; }
                .no-results-tips a { color: #fd7e14; text-decoration: none; font-weight: 600; }
                .no-results-tips a:hover { text-decoration: underline; }
                .no-results-suggestions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 16px; }
                .no-results-suggestions .qs-chip {
                    background: #f1f5f9; border-color: #e2e8f0; color: #475569;
                }
                .no-results-suggestions .qs-chip:hover {
                    background: rgba(253,126,20,.08); border-color: #fd7e14; color: #fd7e14;
                }

                /* ── Topbar résultats ── */
                .results-topbar {
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 16px;
                    margin-bottom: 32px; padding-bottom: 20px;
                    border-bottom: 2px solid #e2e8f0;
                    animation: fadeUp .4s ease-out;
                }
                .results-stats { font-size: 15px; color: #64748b; }
                .results-stats strong { color: #1e293b; font-weight: 700; }
                .results-stats em { color: #fd7e14; font-style: normal; font-weight: 600; }

                .filter-tabs { display: flex; gap: 6px; }
                .filter-tab {
                    display: flex; align-items: center; gap: 7px;
                    padding: 8px 16px;
                    background: white; border: 1.5px solid #e2e8f0;
                    border-radius: 50px; font-size: 13px; font-weight: 600; color: #64748b;
                    cursor: pointer; transition: all .25s;
                    font-family: 'DM Sans', sans-serif;
                }
                .filter-tab:hover { border-color: #fd7e14; color: #fd7e14; background: #fff8f5; }
                .filter-tab.active {
                    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
                    border-color: transparent; color: white;
                    box-shadow: 0 4px 12px rgba(253,126,20,.3);
                }
                .tab-count {
                    background: rgba(255,255,255,.25); border-radius: 50px;
                    padding: 1px 7px; font-size: 11px; font-weight: 700;
                }
                .filter-tab:not(.active) .tab-count {
                    background: #f1f5f9; color: #94a3b8;
                }

                /* ── Blocs résultats ── */
                .results-block { margin-bottom: 48px; animation: fadeUp .5s ease-out; }

                .block-header {
                    display: flex; align-items: center; gap: 12px;
                    margin-bottom: 20px;
                }
                .block-header-icon {
                    width: 38px; height: 38px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center; color: white;
                }
                .pages-icon      { background: linear-gradient(135deg, #198754, #20c997); }
                .formations-icon { background: linear-gradient(135deg, #fd7e14, #dc3545); }

                .block-title {
                    font-family: 'Sora', sans-serif;
                    font-size: 20px; font-weight: 700; color: #1e293b; flex: 1;
                }
                .block-count {
                    background: #f1f5f9; color: #64748b;
                    font-size: 12px; font-weight: 700;
                    padding: 3px 10px; border-radius: 50px;
                }

                /* ── Pages grid ── */
                .pages-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 14px;
                }
                .page-card {
                    display: flex; align-items: center; gap: 16px;
                    padding: 18px 20px;
                    background: white; border-radius: 14px;
                    border: 1.5px solid #e2e8f0;
                    text-decoration: none; color: inherit;
                    transition: all .25s;
                }
                .page-card:hover {
                    border-color: #fd7e14;
                    box-shadow: 0 8px 24px rgba(253,126,20,.1);
                    transform: translateY(-3px);
                }
                .page-card-icon { font-size: 28px; flex-shrink: 0; line-height: 1; }
                .page-card-body { flex: 1; min-width: 0; }
                .page-card-title { font-weight: 700; font-size: 14.5px; color: #1e293b; margin-bottom: 4px; }
                .page-card-desc  { font-size: 12.5px; color: #64748b; line-height: 1.5; }
                .page-card-arrow { color: #cbd5e0; flex-shrink: 0; transition: transform .2s, color .2s; }
                .page-card:hover .page-card-arrow { color: #fd7e14; transform: translateX(3px); }

                /* ── Formations list ── */
                .formations-list { display: flex; flex-direction: column; gap: 12px; }

                .formation-card {
                    display: flex; align-items: flex-start; gap: 18px;
                    background: white; border-radius: 16px;
                    border: 1.5px solid #e2e8f0;
                    padding: 20px 22px;
                    cursor: pointer; transition: all .25s;
                    position: relative; overflow: hidden;
                }
                .formation-card:hover {
                    border-color: #fd7e14;
                    box-shadow: 0 8px 28px rgba(253,126,20,.12);
                    transform: translateY(-3px);
                }

                .fc-accent {
                    position: absolute; left: 0; top: 0; bottom: 0;
                    width: 4px; border-radius: 4px 0 0 4px;
                }

                .fc-icon {
                    width: 44px; height: 44px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0; margin-left: 4px;
                }

                .fc-body { flex: 1; min-width: 0; }
                .fc-title {
                    font-family: 'Sora', sans-serif;
                    font-size: 15.5px; font-weight: 700; color: #1e293b;
                    margin-bottom: 8px; line-height: 1.4;
                }
                .fc-meta { display: flex; gap: 7px; flex-wrap: wrap; margin-bottom: 8px; }
                .fc-tag {
                    font-size: 11.5px; font-weight: 600;
                    padding: 3px 10px; border-radius: 20px;
                }
                .fc-tag-neutral { background: #f1f5f9; color: #64748b; }
                .fc-desc { font-size: 13px; color: #64748b; line-height: 1.6; }

                .fc-cta {
                    display: flex; align-items: center; gap: 6px;
                    font-size: 13px; font-weight: 700; color: #fd7e14;
                    flex-shrink: 0; white-space: nowrap;
                    opacity: 0; transition: opacity .25s, transform .25s;
                    transform: translateX(-4px);
                }
                .formation-card:hover .fc-cta { opacity: 1; transform: translateX(0); }

                /* ── Voir tout ── */
                .see-all-wrapper { margin-top: 20px; text-align: center; }
                .see-all-btn {
                    display: inline-flex; align-items: center; gap: 10px;
                    padding: 14px 28px;
                    background: white; border: 1.5px solid #e2e8f0;
                    border-radius: 50px;
                    font-size: 14px; font-weight: 600; color: #fd7e14;
                    text-decoration: none; transition: all .25s;
                }
                .see-all-btn:hover {
                    background: #fd7e14; color: white; border-color: #fd7e14;
                    box-shadow: 0 8px 24px rgba(253,126,20,.3);
                    transform: translateY(-2px);
                }

                /* ── Responsive ── */
                @media(max-width: 640px) {
                    .search-hero { padding: 100px 16px 64px; }
                    .msb-submit span { display: none; }
                    .msb-submit { padding: 14px 16px; }
                    .results-topbar { flex-direction: column; align-items: flex-start; }
                    .pages-grid { grid-template-columns: 1fr; }
                    .formation-card { flex-wrap: wrap; }
                    .fc-cta { opacity: 1; transform: none; }
                    .filter-tabs { flex-wrap: wrap; }
                }
            `}</style>
        </>
    );
};

export default SearchPage;